import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as Actions from '../store/actions';
import { Dialog, Fetchable } from '../store/appState';
import { RootState } from '../store/rootState';
import * as _ from "lodash";
import { App, SignInState } from '../app';
import { Person } from '../components/shared/PersonPicker';
import { hasItems } from '../utils/collections';
import { ensureProtocol } from '../utils/webPage';
import { problemStates } from '../config/problemStates';
import { ideaStates } from '../config/ideaStates';
import { useHistory } from 'react-router-dom';
import { DetailViewType, UrlParam } from '../constants/urlParams';
import { isNumber } from 'lodash';
import { Map } from "../store/appState";
import { Author, ChildPost, getNeedMoreInfoComments, HistoryItem, PostAction, PostType, PrincipalId, RootPost, StateTag, Tag } from '../RootPost';
import { FeedbackTag } from '../PublicFeedback';
import { isWorkaround } from '../components/Post/Details/RichSolution';
import { instance } from '../config/serverConfig';

export enum TimelineItemType {
    routeInfo = "routeInfo",
    stateChange = "stateChange",
    solution = "solution",
    comment = "comment",
    workaround = "workaround",
}

// Any state chages and comment/solution additions which hapenned within this duration will be merged in timeline
const MaxStateToCommentChangeDeltaMS = 5000;

const vsLogo = instance.isInternal ? `https://aka.ms/dcoxo/avatar?userId=7` : `https://aka.ms/dc/avatar?userId=7`;
const feedbackBotName = 'Feedback Bot';

export interface TimelineItem {
    id?: string | number;
    timestamp: Date;
    type: TimelineItemType;
    logoImageUrl: string;
    authorName: string;
    logoTooltip?: string;
    timelineDepth: number;
    data: ChildPost | HistoryItem | string[];
    children: TimelineItem[];

    associatedItem?: TimelineItem;
    ignore?: boolean;
}

export interface TicketDetails {
    ticket: Fetchable<RootPost>;
    timelineItems: TimelineItem[];
    solutionTimelineItems: TimelineItem[];
    pinnedItems: TimelineItem[];
    relevantPersons: Person[];
    lastStateChangeDaysAgo: number;
    isTicketReadOnly: boolean;
    canAddSolution: boolean;
    canAddComment: boolean;
    canAddReaction: boolean;
    nmiRequestComments: ChildPost[];
    originalNmiComment: ChildPost;
    mostRelevantFixedInRelease: string;
    fillMissingInfoClickAttempt: number;
    fillMissingInfoClicked: () => void;
}

const isValidId = (answerHubId: number): boolean => isNumber(answerHubId);

const getValidTicketDetail = <T>(answerHubId: number, detailsCollection: Map<number, Fetchable<T>>): Fetchable<T> | null => {
    // eslint-disable-next-line security/detect-object-injection
    return isValidId(answerHubId) ? detailsCollection[answerHubId] : null;
}

export const useTicketFromDescendantComment = (descendantCommentId: number): Fetchable<RootPost> | null => {
    const ticket = useSelector((state: RootState) => getValidTicketDetail(descendantCommentId, state.appState.ticketsByCommentId));

    useEffect(() => {
        if (!ticket) {
            Actions.loadTicketFromDescendantComment(descendantCommentId);
        }
    }, [ticket]);

    return ticket;
}

// Return spcified ticket. Fetch if it isn't already fetched
export const useTicketWithDetails = (answerHubId: number): TicketDetails => {
    const signinState = useSelector((state: RootState) => state.appState.signInState);
    const settingsTemplate = useSelector((state: RootState) => state.appState.settings?.template);
    const ticket: Fetchable<RootPost> | null = useSelector((state: RootState) => getValidTicketDetail(answerHubId, state.appState.ticketsById));

    const signInState = useSelector((state: RootState) => state.appState.signInState);
    const fillMissingInfoClickAttempt = useRef<number>(0);
    const refSignInState = useRef<SignInState>(null);

    const vsid = useMemo(
        () => App.getVsid(settingsTemplate)
        , [settingsTemplate?.tags]
    );

    useEffect(() => {
        if (!ticket || refSignInState.current !== signInState) {
            // sign in/out  could require few field updates to ticket
            refSignInState.current = signInState;
            if (signinState !== SignInState.Checking && signinState !== SignInState.Executing) {
                Actions.loadTicket(answerHubId);
            }
        }
    }, [ticket, vsid, signinState]);

    const relevantPersons: Person[] = useMemo(
        () => getRelevantPersons(ticket)
        , [ticket?.data]);

    const nmiRequestComments: ChildPost[] = useMemo(
        () => {
            if (!ticket || ticket.isFetching || ticket.hasErrors) {
                return [];
            }

            return getNeedMoreInfoComments(ticket.data);
        }
        , [ticket?.data]);


    const lastStateChangeDaysAgo = useMemo(
        (): (number | null) => {
            const history = ticket?.data?.partialHistory ?? [];
            const idx = _.findLastIndex(history, (s: HistoryItem) => s.status === ticket?.data?.state);
            // eslint-disable-next-line security/detect-object-injection
            const lastStateInfo = idx === -1 ? null : history[idx];
            if (lastStateInfo) {
                const diff = (new Date()).getTime() - lastStateInfo.date.getTime();
                const diffInDays = Math.round(diff / (1000 * 3600 * 24));
                return diffInDays;
            }
            return null;
        }
        , [ticket?.data]
    );

    const isTicketReadOnly: boolean = App.isTicketReadonly(ticket?.data?.state, lastStateChangeDaysAgo);

    const ticketAuthorLogo = ticket?.data?.createdByImageLink;

    const timelineItems = useMemo(
        () => getTimelineItems(ticket?.data, ticketAuthorLogo, ticket?.data?.author.name)
        , [ticket?.data, ticketAuthorLogo, ticket?.data?.author.name]
    );

    const solutionTimelineItems = useMemo(
        () => timelineItems
            .filter(item => item.type === TimelineItemType.solution)
            .sort((a, b) => {
                // Special ordering to show most appropriate solutions/workarounds at the top

                const solnA: ChildPost = a.data as ChildPost;
                const solnB: ChildPost = b.data as ChildPost;

                const isAFinalSoln = App.CouldReactivateSolutionEver(solnA);
                const isBFinalSoln = App.CouldReactivateSolutionEver(solnB);

                // Show final Microsoft solutions at the top
                // Normally there will be only one such solution only after ticket is closed
                if (isAFinalSoln) {
                    return -1;
                }
                if (isBFinalSoln) {
                    return 1;
                }

                const isAMsft = solnA.author.name?.indexOf('[MSFT]') !== -1;
                const isBMsft = solnB.author.name?.indexOf('[MSFT]') !== -1;

                // Keep MSFT solutions & workarounds at top
                if (isAMsft && !isBMsft) {
                    return -1;
                }
                if (!isAMsft && isBMsft) {
                    return 1;
                }

                // Both solutions/workarounds are either from MSFT or external users

                // Show solutions before workarounds
                const isAWorkaround = isWorkaround(solnA);
                const isBWorkaround = isWorkaround(solnB);

                if (isAWorkaround && !isBWorkaround) {  // i.e. b is a solution but a isn't
                    return 1;
                }
                if (!isAWorkaround && isBWorkaround) {  // i.e. a is a solution but b isn't
                    return -1;
                }

                // Both are either solutions or workarounds

                // Sort reverse chronologically
                return b.timestamp.getTime() - a.timestamp.getTime();
            })
        , [timelineItems]
    );

    // @TODO: Get any other items to pin to timeline
    const pinnedItems: TimelineItem[] = useMemo(
        () => {
            return timelineItems.filter(s => {
                if (!(s.data as ChildPost).parentId) {
                    return false;
                }

                const child: ChildPost = s.data as ChildPost;
                return App.CouldReactivateSolutionEver(child);
            });
        }
        , [timelineItems]
    );

    const canAddComment: boolean = useMemo(
        () => App.canAddComment(ticket?.data, isTicketReadOnly)
        , [problemStates, ideaStates, ticket?.data?.state, isTicketReadOnly]
    );

    const canAddSolution: boolean = useMemo(
        () => App.canAddSolution(ticket?.data, isTicketReadOnly)
        , [problemStates, ticket?.data?.state, isTicketReadOnly]
    );

    const canAddReaction: boolean = useMemo(
        () => App.canAddReaction(ticket?.data, isTicketReadOnly)
        , [problemStates, ticket?.data?.state, isTicketReadOnly]
    );

    const originalNmiComment = useMemo(() => {
        if (!hasItems(nmiRequestComments)) {
            return null;
        }
        return nmiRequestComments[0];
    }, [nmiRequestComments]);

    const history = useHistory();

    const fillMissingInfoClicked = useCallback(
        () => {
            fillMissingInfoClickAttempt.current++;
            Actions.setDialog(Dialog.comment);
            const urlParams = new URLSearchParams(history.location.search);
            urlParams.set(UrlParam.ProvideMoreInfo, "true");
            urlParams.set(UrlParam.DetailViewType, DetailViewType.all);
            history.push(`${history.location.pathname}?${urlParams.toString()}`);
        }
        , [history.location]);

    const mostRelevantFixedInRelease = useMemo(
        () => getMostRelevantFixedInRelease(ticket?.data?.tags),
        [ticket?.data?.tags]
    );

    return {
        ticket, relevantPersons,
        isTicketReadOnly, lastStateChangeDaysAgo, canAddSolution, canAddComment, canAddReaction,
        pinnedItems, timelineItems, solutionTimelineItems,
        nmiRequestComments, originalNmiComment, fillMissingInfoClicked, fillMissingInfoClickAttempt: fillMissingInfoClickAttempt.current,
        mostRelevantFixedInRelease
    };
}

export const getMostRelevantFixedInRelease = (tags: FeedbackTag[]): string => {
    if (!hasItems(tags)) {
        return null;
    }

    const sorted = _.cloneDeep(tags.filter(x => x.value.toLowerCase().indexOf("fixed in") !== -1));

    if (!hasItems(sorted)) {
        return null;
    }

    sorted.sort((a, b) => {
        const aLower = a.value.toLowerCase();
        const bLower = b.value.toLowerCase();
        const aIsForPreview = aLower.indexOf("preview");
        const bIsForPreview = bLower.indexOf("preview");
        if (aIsForPreview && !bIsForPreview) {
            return 1;
        }
        if (!aIsForPreview && bIsForPreview) {
            return -1;
        }

        return aLower.localeCompare(bLower);
    });

    return sorted[0].value
        .replace(/fixed[\s-]in:\s*/gi, "")
        .replace(/visual studio 20\d\d/gi, "");
}

const getRelevantPersons = (ticket: Fetchable<RootPost>): Person[] => {
    const persons: Person[] = [];
    if (!ticket || ticket.isFetching || ticket.hasErrors) {
        return persons;
    }

    const t = ticket.data;
    if (t.author.name) {
        addPerson(persons, { name: t.author.name, userInfoUrl: ensureProtocol(t.createdByUrl), profileImageUrl: ensureProtocol(t.createdByImageLink) });
    }

    addPersonsFromChildren(persons, t.children);
    return persons;
}

const addPersonsFromChildren = (persons: Person[], children: ChildPost[]): void => {
    if (children.length === 0) {
        return;
    }

    children.forEach(child => {
        addPerson(persons, { name: child.author.name, userInfoUrl: App.GetUserProfileLink(child.author.id), profileImageUrl: child.createdByImageLink });
        addPersonsFromChildren(persons, child.children);
    });
}

const getTimelineItems = (ticket: RootPost | null, ticketAuthorLogo?: string, ticketAuthorName?: string): TimelineItem[] => {
    if (ticket == null) {
        return [];
    }

    let items: TimelineItem[] = [];

    items = items.concat(childrenToTimeline(ticket.children, 0));
    items = items.concat(statesToTimeline(ticket.partialHistory, 0, ticketAuthorLogo, ticketAuthorName));

    items.sort((a, b) => {
        return a.timestamp.getTime() - b.timestamp.getTime();
    });

    // place route info at the top
    items = routeInfoToTimeline(ticket.partialHistory, 0).concat(items);

    // merge state changes with comments/solutions if they hapenned too close to each other
    // nikolami: 2024-02-16: start from 1 to skip the route info
    for (let i = 1; i < items.length; i++) {
        // eslint-disable-next-line security/detect-object-injection
        const item = items[i];

        if (item.type === TimelineItemType.stateChange) continue;

        // Check if previous item is a state change and if it happenned within the threshold to merge
        if (i !== 0) {
            const prevItem = items[i - 1];
            if (prevItem.type === TimelineItemType.stateChange && !prevItem.ignore) {
                const delta = item.timestamp.getTime() - prevItem.timestamp.getTime();
                if (delta < MaxStateToCommentChangeDeltaMS) {
                    prevItem.ignore = true;   // previous state change happened too close to this comment/solution so merge it with this
                    item.associatedItem = prevItem;
                }
            }
        }

        if (item.associatedItem) continue;  // already merged with previous state change item so skip further lookups

        // Check if next item is a state change and if it happenned within the threshold to merge
        if (i < items.length - 1) {
            const nextItem = items[i + 1];
            if (nextItem.type === TimelineItemType.stateChange) {
                const delta = nextItem.timestamp.getTime() - item.timestamp.getTime();
                if (delta < MaxStateToCommentChangeDeltaMS) {
                    nextItem.ignore = true;     // next state change happened too close to this comment/solution so merge it with this
                    item.associatedItem = nextItem;
                }
            }
        }
    }

    const finalItems = items.filter(x => !x.ignore);
    return finalItems;
}

const childrenToTimeline = (children: ChildPost[], timelineDepth: number): TimelineItem[] => {
    return children.map(x => ({
        type: ((x.type === PostType.comment) ? TimelineItemType.comment : TimelineItemType.solution) as TimelineItemType,
        id: x.id,
        timestamp: x.createdDate,
        logoImageUrl: x.createdByImageLink,
        authorName: x.author.name,
        data: x,
        timelineDepth,
        children: childrenToTimeline(x.children, timelineDepth + 1),
    } as TimelineItem));
}

const statesToTimeline = (states: HistoryItem[], timelineDepth: number, ticketAuthorLogo?: string, ticketAuthorName?: string): TimelineItem[] => {
    return states.filter(item => item.action === PostAction.add_tag).map(x => ({
        type: TimelineItemType.stateChange,
        id: `stateChange-${x.date.getTime()}`,
        timestamp: x.date,
        logoImageUrl: x.tagId === StateTag.new ? ticketAuthorLogo : vsLogo,
        authorName: x.tagId === StateTag.new ? ticketAuthorName : feedbackBotName,
        data: x,
        timelineDepth,
        children: null
    } as TimelineItem));
}

const routeInfoToTimeline = (states: HistoryItem[], timelineDepth: number, ticketAuthorLogo?: string, ticketAuthorName?: string): TimelineItem[] => {
    const routeInfo: HistoryItem[] = states.filter(item => item.action === PostAction.auto_tag || item.action === PostAction.route);
    if (routeInfo.length === 0) {
        return [];
    }

    const actions: string[] = routeInfo.map(x => x.new);
    const firstItem = routeInfo[0];

    return [{
        type: TimelineItemType.routeInfo,
        id: `routeInfo-${firstItem.date.getTime()}`,
        timestamp: firstItem.date,
        logoImageUrl: vsLogo,
        authorName: feedbackBotName,
        data: actions,
        timelineDepth,
        children: null
    } as TimelineItem];
}


const excludedPersonNames = ["Feedback Bot"];

const addPerson = (persons: Person[], personToAdd: Person): void => {
    if (!persons.some(x => x.name === personToAdd.name) && excludedPersonNames.indexOf(personToAdd.name) === -1) {
        persons.push(personToAdd);
    }
}
